import { useState } from 'react'
import { Mail, MessageSquare } from 'lucide-react'
import Card from '@/components/layout/Card'
import Modal from '@/components/ui/Modal'
import RichText from '@/components/RichText'
import { useListCampaigns } from '@/features/lists/useLists'
import { tokensInWords } from '@/features/messaging/tokens'
import { formatShortDate } from '@/lib/dealFormat'
import type { ListCampaign } from '@/api/lists'

function pct(part: number, of: number): string {
  return of > 0 ? `${Math.round((part / of) * 100)}%` : '—'
}

/**
 * The emails and texts sent to this list — each with what went out and how it was received (sent,
 * opened, clicked, bounced), as on HubSpot's email performance page.
 */
export default function ListCampaignsCard({ listId }: { listId: string }) {
  const { data: campaigns } = useListCampaigns(listId)
  const [viewing, setViewing] = useState<ListCampaign | null>(null)
  return (
    <Card title="Campaigns">
      {!campaigns || campaigns.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing sent to this list yet. Use Send above — each send shows here with its opens and clicks.</p>
      ) : (
        <ul className="space-y-3">
          {campaigns.map((c) => (
            <li key={c.id} className="text-sm">
              <button
                type="button"
                onClick={() => setViewing(c)}
                className="flex items-start gap-2 text-left w-full group"
                title="See what was sent"
              >
                {c.channel === 'sms' ? (
                  <MessageSquare size={15} className="mt-0.5 text-brand-primary shrink-0" />
                ) : (
                  <Mail size={15} className="mt-0.5 text-brand-primary shrink-0" />
                )}
                <span className="min-w-0">
                  <span className="block font-medium text-brand-darkest truncate group-hover:text-brand-primary">
                    {c.channel === 'sms' ? 'SMS' : tokensInWords(c.subject || '(no subject)')}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {formatShortDate(c.createdAt)}
                    {c.sentByName ? ` · ${c.sentByName}` : ''}
                  </span>
                </span>
              </button>
              <dl className="mt-1.5 ml-6 grid grid-cols-3 gap-2 text-center">
                <Stat label="Sent" value={`${c.sent}`} hint={c.sent < c.recipients ? `of ${c.recipients}` : undefined} />
                {c.channel === 'email' ? (
                  <>
                    <Stat label="Opened" value={pct(c.opened, c.sent)} hint={c.opened > 0 ? `${c.opened}` : undefined} />
                    <Stat label="Clicked" value={pct(c.clicked, c.sent)} hint={c.clicked > 0 ? `${c.clicked}` : undefined} />
                  </>
                ) : (
                  <span className="col-span-2" />
                )}
              </dl>
              {c.bounced > 0 && <p className="ml-6 mt-1 text-xs text-danger">{c.bounced} didn’t arrive (bounced)</p>}
            </li>
          ))}
        </ul>
      )}
      {viewing && (
        <Modal
          title={viewing.channel === 'sms' ? 'SMS sent to this list' : tokensInWords(viewing.subject || '(no subject)')}
          onClose={() => setViewing(null)}
          size="lg"
        >
          <p className="text-xs text-gray-500 mb-3">
            Sent {formatShortDate(viewing.createdAt)}
            {viewing.sentByName ? ` by ${viewing.sentByName}` : ''} to {viewing.sent} of {viewing.recipients} people. Names in
            [brackets] were filled in for each person.
          </p>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            {viewing.channel === 'sms' ? (
              <p className="whitespace-pre-wrap text-sm text-gray-700">{tokensInWords(viewing.body)}</p>
            ) : (
              <RichText html={tokensInWords(viewing.body)} variant="email" className="text-sm text-gray-700" />
            )}
          </div>
        </Modal>
      )}
    </Card>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-gray-50 border border-gray-100 px-1 py-1.5">
      <dt className="text-[10px] uppercase tracking-wider text-gray-400">{label}</dt>
      <dd className="text-sm font-semibold text-brand-darkest tabular-nums">
        {value}
        {hint && <span className="ml-1 text-[11px] font-normal text-gray-400">{hint}</span>}
      </dd>
    </div>
  )
}
